/* eslint-disable import/no-anonymous-default-export */

import { useCallback } from 'react';
import _empty from 'lodash/isEmpty';
import _get from 'lodash/get';
import _omit from 'lodash/omit';
import { GAME_NAME } from 'commons/models/common';
import { EMPTY_STRING } from 'commons/constants/immutable';
import { GAME_KEY_MAP } from 'pages/admin/zooKeeper/utils/helper';
import zkUseService from '../../../admin/zooKeeper/utils/useService';
import {
	convert1dListInto2d,
	createCompleteObject,
	getArrayKeys,
	getExceptSegmentViewValueByKeys,
	getSegmentKeys,
	getSegmentViewValueByKeys
} from '../utils/formDataParser';
import {
	EHomePageViewType,
	HOME_PAGE_VIEW_V4,
	IHomePageModifiedData,
	IHomePageTranslatedKeys,
	IHomePageV4View,
	IHomePageZkData,
	ISegmentBasedUser,
	ITObject,
	MAX_ALLOWED_ID,
	SERVICE_NAME,
	USER_NAME
} from '../utils/constants';

const useList = () => {
	const { getKeyValue, updateKeyValue } = zkUseService();

	const generatedIds: number[] = [];
	const incCount = (): number => {
		const generatedId: number = Math.floor(Math.random() * MAX_ALLOWED_ID);
		if (generatedIds.includes(generatedId)) {
			return incCount();
		}
		generatedIds.push(generatedId);
		return generatedId;
	};

	const formatHomePageViewData = (result: IHomePageV4View): IHomePageModifiedData => {
		if (_empty(result)) {
			return {} as IHomePageModifiedData;
		}
		const completeViewStringified: string = JSON.stringify(result);
		const completeViewExceptSegmentUserParsed: IHomePageV4View = _omit(
			JSON.parse(completeViewStringified),
			EHomePageViewType.SEGMENT_BASED_USER
		) as IHomePageV4View;
		const segmentBasedView: ISegmentBasedUser[] = JSON.parse(completeViewStringified).segmentBasedUser;
		const segmentKeys: string[] = getSegmentKeys(segmentBasedView);
		const allOtherKeys: string[] = getArrayKeys(completeViewExceptSegmentUserParsed);
		const keys: IHomePageTranslatedKeys = {
			segmentView: segmentKeys,
			nonSegmentView: allOtherKeys
		};
		const hydratedViewObj: IHomePageModifiedData = {
			viewKeys: keys,
			segmentView: getSegmentViewValueByKeys(segmentKeys, segmentBasedView),
			nonSegmentView: getExceptSegmentViewValueByKeys(allOtherKeys, completeViewExceptSegmentUserParsed)
		};
		return hydratedViewObj;
	};

	const getHomePageViewKeyName = (): string => {
		return HOME_PAGE_VIEW_V4;
	};

	const getHomePageV4ViewData = useCallback(async (): Promise<IHomePageModifiedData> => {
		const keyName: string = getHomePageViewKeyName();
		const result: {
			value: IHomePageV4View;
			success: boolean;
		} = await getKeyValue({ game: GAME_NAME.LUDO, keyName, serviceName: SERVICE_NAME });
		const data: IHomePageModifiedData = formatHomePageViewData(result?.value);
		return data;
	}, []);

	const handleConfirmSubmission = (existingHomePageV4View: IHomePageModifiedData, updatedHomePageV4View: IHomePageModifiedData) => {
		let count = 0;
		const newHomePageV4View: IHomePageModifiedData = {};
		Object.keys(existingHomePageV4View).forEach((viewType) => {
			if (!newHomePageV4View[viewType as keyof IHomePageModifiedData]) {
				newHomePageV4View[viewType as keyof IHomePageModifiedData] = {} as IHomePageTranslatedKeys & ITObject<string[][]>;
			}
			Object.keys(existingHomePageV4View[viewType as keyof IHomePageModifiedData]).forEach((viewKey) => {
				if (!_empty(viewKey)) {
					newHomePageV4View[viewType][viewKey] = existingHomePageV4View[viewType as keyof IHomePageModifiedData][viewKey];
				}
			});
		});
		Object.keys(updatedHomePageV4View).forEach((viewType) => {
			if (!newHomePageV4View[viewType as keyof IHomePageModifiedData]) {
				newHomePageV4View[viewType as keyof IHomePageModifiedData] = {} as IHomePageTranslatedKeys & ITObject<string[][]>;
			}
			Object.keys(updatedHomePageV4View[viewType]).forEach((viewKey) => {
				if (updatedHomePageV4View[viewType][viewKey] && existingHomePageV4View[viewType][viewKey]) {
					newHomePageV4View[viewType][viewKey] = convert1dListInto2d(updatedHomePageV4View[viewType][viewKey]);
					count += 1;
				}
			});
		});
		if (!count) {
			return {};
		}
		const finalView = createCompleteObject(newHomePageV4View.segmentView, newHomePageV4View.nonSegmentView);
		return finalView;
	};

	const requestUpdateHomePageV4View = useCallback(async (appName: string, user, gameName: string, homePageV4View: any) => {
		const keyName: string = getHomePageViewKeyName();
		const zkDataToUpdate: IHomePageZkData<IHomePageV4View> = {
			keyName,
			serviceName: SERVICE_NAME,
			agent: _get(user, USER_NAME, EMPTY_STRING),
			desc: `Updated ${keyName}`,
			data: homePageV4View,
			game: GAME_KEY_MAP[appName]
		};
		const response = await updateKeyValue(zkDataToUpdate);
		return response;
	}, []);

	return {
		getHomePageV4ViewData,
		handleConfirmSubmission,
		requestUpdateHomePageV4View,
		incCount
	};
};

export default useList;
